import { useState, useEffect, useCallback, useRef } from "react";

let _uid = 0;
const uid = () => `${++_uid}`;

// ── Master lists ──────────────────────────────────────────────────────────────
const RAGAMS = [
  "Abhogi","Anandabhairavi","Arabhi","Atana","Begada","Bhairavi","Bilahari",
  "Bhoopalam","Chakravakam","Charukesi","Desh","Devagandhari","Dheerasankarabharanam",
  "Gourimanohari","Gowla","Harikambhoji","Hamsadhvani","Hindolam","Kamas","Kanakangi",
  "Karaharapriya","Kedaragowla","Keeravani","Kokilapriya","Latangi","Madhyamavati",
  "Mayamalavagowla","Mechakalyani","Mohanam","Mukhari","Nattai","Natabhairavi",
  "Pantuvarali","Punnagavarali","Ragavardhini","Revati","Rupavati","Sahana","Sarasangi",
  "Saveri","Shanmukhapriya","Simhendramadhyamam","Sindhu Bhairavi","Sriranjani",
  "Suddha Saveri","Suryakantam","Todi","Varali","Varunapriya","Yamunakalyani",
  "Kharaharapriya","Natkapriya","Gamanashrama","Hemavati","Dharmavati","Neetimati",
  "Chitrambari","Rishabhapriya","Rasikapriya","Kosalam","Nasikabhushani","Kamavardhini",
  "Shankarabharanam","Salagam","Jhalavarali","Ramapriya","Vishwambhari","Latangi",
  "Vachaspati","Gayakapriya","Mararanjani","Gangeyabhushani","Vagadheeswari","Shulini",
  "Suvarnangi","Divyamani","Dhavalambari","Namanarayani",
];

const TALAMS = [
  "Adi","Rupaka","Misra Chapu","Khanda Chapu","Tisra Chapu",
  "Ata","Jhampai","Eka","Dhruva","Matya","Triputa",
  "Tisra Jati Eka","Chatusra Jati Eka","Khanda Jati Eka","Misra Jati Eka",
  "Tisra Jati Rupaka","Chatusra Jati Rupaka","Khanda Jati Rupaka",
  "Tisra Jati Triputa","Chatusra Jati Triputa","Khanda Jati Triputa",
  "Tisra Jati Matya","Chatusra Jati Matya","Khanda Jati Matya",
  "Tisra Jati Ata","Chatusra Jati Ata","Khanda Jati Ata","Misra Jati Ata",
  "Tisra Jati Dhruva","Chatusra Jati Dhruva","Khanda Jati Dhruva",
  "Tisra Jati Jhampa","Chatusra Jati Jhampa","Khanda Jati Jhampa",
];

// ── Language data ─────────────────────────────────────────────────────────────
const SWARA_KEYS = ["s", "r", "g", "m", "p", "d", "n"];
const SWARA_MAPS = {
  en: ["S", "R", "G", "M", "P", "D", "N"],
  ta: ["ஸ", "ரி", "க", "ம", "ப", "த", "நி"],
};
const LANG_FONTS = {
  en: "'Times New Roman',Times,serif",
  ta: "'Noto Serif Tamil',serif",
};
const LANGS = [
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
];

const SUP = { "0":"⁰","1":"¹","2":"²","3":"³","4":"⁴","5":"⁵","6":"⁶","7":"⁷","8":"⁸","9":"⁹" };
const SUB = { "0":"₀","1":"₁","2":"₂","3":"₃","4":"₄","5":"₅","6":"₆","7":"₇","8":"₈","9":"₉" };
const GAMAKA_TYPES = ["OSC", "GLD", "ART", "GRC"];
const INK = "#1A0800";
const CELL_W = 15;

const HEADER_FIELDS = [
  { key: "songname",   label: "Song Name" },
  { key: "composer",   label: "Composer" },
  { key: "ragam",      label: "Ragam",      list: "ragam-dl" },
  { key: "talam",      label: "Talam",      list: "talam-dl" },
  { key: "arohanam",   label: "Arohanam" },
  { key: "avarohanam", label: "Avarohanam" },
];

const INIT_ROW  = () => ({ id: uid(), tokens: [], sahityam: "" });
const INIT_SECS = () => [
  { id: "pallavi",    name: "Pallavi",    rows: [INIT_ROW()] },
  { id: "anupallavi", name: "Anupallavi", rows: [INIT_ROW()] },
  { id: "charanam",   name: "Charanam",   rows: [INIT_ROW()] },
];
const INIT_HDR = () => ({
  songname: "", composer: "", ragam: "", talam: "", arohanam: "", avarohanam: "",
});

const STORE_KEY   = "carnatikala_v7";
const LIBRARY_KEY = "carnatikala_lib_v2";

const PAD_TOP = 18;

// ── Helpers ───────────────────────────────────────────────────────────────────
function getSwaraText(sk, lang, caps) {
  const i = SWARA_KEYS.indexOf(sk);
  if (i < 0) return sk;
  const b = SWARA_MAPS[lang]?.[i] ?? SWARA_MAPS.en[i];
  return lang === "en" && !caps ? b.toLowerCase() : b;
}

function groupTokens(tokens) {
  const out = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.gid) {
      const gid = t.gid;
      const sf = i;
      const items = [t];
      while (i + 1 < tokens.length && tokens[i + 1].gid === gid) {
        i++;
        items.push(tokens[i]);
      }
      out.push({ kind: "arc", dir: t.gdir, gt: t.gamakaType, items, startFlat: sf, endFlat: i });
    } else {
      out.push({ kind: "tok", tok: t, flatIdx: i });
    }
    i++;
  }
  return out;
}

// ── BlinkCursor ───────────────────────────────────────────────────────────────
function BlinkCursor() {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const iv = setInterval(() => setOn((v) => !v), 530);
    return () => clearInterval(iv);
  }, []);
  return (
    <span
      style={{
        display: "inline-block",
        width: 2,
        height: 18,
        background: on ? "#C97030" : "transparent",
        verticalAlign: "bottom",
        flexShrink: 0,
        marginBottom: 2,
      }}
    />
  );
}

// ── GamakaLine ────────────────────────────────────────────────────────────────
function GamakaLine({ dir, gt, width }) {
  const above = dir === "above";
  const H = 10;
  const hw = Math.min(5, width * 0.12);
  const lineY = above ? 1 : H - 1;
  const lbl = gt || "";
  const lblW = lbl.length * 5 + 4;
  const mid = width / 2;
  const lEnd = mid - lblW / 2 - 2;
  const rStart = mid + lblW / 2 + 2;

  const lPath = above
    ? `M0,${H} L0,${hw} Q0,${lineY} ${hw},${lineY} L${lEnd},${lineY}`
    : `M0,0 L0,${H - hw} Q0,${lineY} ${hw},${lineY} L${lEnd},${lineY}`;
  const rPath = above
    ? `M${width},${H} L${width},${hw} Q${width},${lineY} ${width - hw},${lineY} L${rStart},${lineY}`
    : `M${width},0 L${width},${H - hw} Q${width},${lineY} ${width - hw},${lineY} L${rStart},${lineY}`;

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: above ? 0 : undefined,
        bottom: above ? undefined : 0,
        height: H,
        pointerEvents: "none",
        zIndex: 3,
      }}
    >
      <svg
        width={width}
        height={H}
        style={{ position: "absolute", top: 0, left: 0, overflow: "visible" }}
      >
        <path
          d={lPath}
          fill="none"
          stroke={INK}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={rPath}
          fill="none"
          stroke={INK}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {lbl && (
          <>
            <rect
              x={mid - lblW / 2}
              y={above ? lineY - 5 : lineY - 5}
              width={lblW}
              height={10}
              fill="#FFFFFF"
              stroke="none"
            />
            <text
              x={mid}
              y={above ? lineY + 3 : lineY + 3}
              textAnchor="middle"
              fontSize="7"
              fontWeight="bold"
              fontFamily="system-ui"
              fill="#5A3010"
            >
              {lbl}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

// ── SwaraSpan ─────────────────────────────────────────────────────────────────
function SwaraSpan({ t, lang, caps, dataIdx }) {
  const txt = getSwaraText(t.swaraKey, lang, caps);
  const fontSize = lang === "en" ? "14px" : "13px";
  return (
    <span
      data-token-idx={dataIdx}
      style={{
        display: "inline-block",
        position: "relative",
        verticalAlign: "bottom",
        minWidth: CELL_W,
        textAlign: "center",
        paddingTop: PAD_TOP,
        paddingBottom: t.octave === "down" ? 7 : 2,
        userSelect: "none",
      }}
    >
      {t.octave === "up" && (
        <span
          style={{
            position: "absolute",
            top: 2,
            left: "50%",
            transform: "translateX(-50%)",
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: INK,
            display: "block",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          }}
        />
      )}
      {t.speed >= 2 && (
        <span
          style={{
            position: "absolute",
            top: 8,
            left: "14%",
            right: "14%",
            height: 1,
            background: INK,
            display: "block",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          }}
        />
      )}
      {t.speed >= 1 && (
        <span
          style={{
            position: "absolute",
            top: t.speed >= 2 ? 12 : 9,
            left: "14%",
            right: "14%",
            height: 1,
            background: INK,
            display: "block",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          }}
        />
      )}
      <span
        style={{
          fontFamily: LANG_FONTS[lang] || LANG_FONTS.en,
          fontSize,
          color: INK,
          whiteSpace: "nowrap",
          lineHeight: 1.2,
          display: "block",
        }}
      >
        {txt}
        {t.sup && (
          <sup
            style={{
              fontSize: "0.78em",
              lineHeight: 0,
              color: INK,
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
            }}
          >
            {SUP[t.sup]}
          </sup>
        )}
        {t.sub && (
          <sub
            style={{
              fontSize: "0.78em",
              lineHeight: 0,
              color: INK,
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
            }}
          >
            {SUB[t.sub]}
          </sub>
        )}
      </span>
      {t.octave === "down" && (
        <span
          style={{
            position: "absolute",
            bottom: 1,
            left: "50%",
            transform: "translateX(-50%)",
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: INK,
            display: "block",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          }}
        />
      )}
    </span>
  );
}

// ── SepSpan ───────────────────────────────────────────────────────────────────
function SepSpan({ t, dataIdx }) {
  if (t.text === "\n") return null;
  const isDanda = t.text === "|" || t.text === "||";
  return (
    <span
      data-token-idx={dataIdx}
      style={{
        display: "inline-block",
        verticalAlign: "bottom",
        minWidth: isDanda ? CELL_W : 10,
        textAlign: "center",
        paddingTop: PAD_TOP,
        paddingBottom: 2,
        fontFamily: "'Times New Roman',serif",
        fontSize: "14px",
        fontWeight: "bold",
        color: INK,
        lineHeight: 1.2,
        userSelect: "none",
      }}
    >
      {t.text === " " ? "\u00A0" : t.text}
    </span>
  );
}

// ── ArcGroup ──────────────────────────────────────────────────────────────────
function ArcGroup({ dir, gt, items, lang, caps, startFlat, onClick }) {
  const above = dir === "above";
  const ref = useRef(null);
  const [w, setW] = useState(60);

  useEffect(() => {
    if (ref.current) setW(ref.current.offsetWidth);
  });

  return (
    <span
      ref={ref}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "flex-end",
        position: "relative",
        verticalAlign: "bottom",
        paddingTop: above ? 12 : 0,
        paddingBottom: above ? 0 : 12,
        margin: 0,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <GamakaLine dir={dir} gt={gt} width={w || 60} />
      {items.map((t, i) =>
        t.type === "swara" ? (
          <SwaraSpan key={t.id} t={t} lang={lang} caps={caps} dataIdx={startFlat + i} />
        ) : (
          <SepSpan key={t.id} t={t} dataIdx={startFlat + i} />
        )
      )}
    </span>
  );
}

// ── Key Icons ─────────────────────────────────────────────────────────────────
const TaraIcon = () => (
  <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
    <span style={{ width: 4, height: 4, borderRadius: "50%", background: "currentColor", display: "block" }} />
    <span style={{ fontSize: "0.6rem", fontWeight: 900, lineHeight: 1 }}>▲</span>
  </span>
);

const ManDrIcon = () => (
  <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
    <span style={{ fontSize: "0.6rem", fontWeight: 900, lineHeight: 1 }}>▼</span>
    <span style={{ width: 4, height: 4, borderRadius: "50%", background: "currentColor", display: "block" }} />
  </span>
);

const SngLine = () => (
  <span style={{ display: "block", width: 14, height: 1, background: "currentColor", borderRadius: 1 }} />
);

const DblLine = () => (
  <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <span style={{ width: 14, height: 1, background: "currentColor", display: "block", borderRadius: 1 }} />
    <span style={{ width: 14, height: 1, background: "currentColor", display: "block", borderRadius: 1 }} />
  </span>
);

const SupIcon = () => (
  <span style={{ fontFamily: "'Times New Roman',serif", fontSize: "11px" }}>
    S<sup style={{ fontSize: "0.68em" }}>x</sup>
  </span>
);

const SubIcon = () => (
  <span style={{ fontFamily: "'Times New Roman',serif", fontSize: "11px" }}>
    S<sub style={{ fontSize: "0.68em" }}>x</sub>
  </span>
);

const ArcAbv = () => (
  <span
    style={{
      display: "inline-block",
      width: 16,
      height: 7,
      borderTop: "2px solid currentColor",
      borderLeft: "2px solid currentColor",
      borderRight: "2px solid currentColor",
      borderRadius: "50% 50% 0 0 / 7px 7px 0 0",
    }}
  />
);

const ArcBlw = () => (
  <span
    style={{
      display: "inline-block",
      width: 16,
      height: 7,
      borderBottom: "2px solid currentColor",
      borderLeft: "2px solid currentColor",
      borderRight: "2px solid currentColor",
      borderRadius: "0 0 50% 50% / 0 0 7px 7px",
    }}
  />
);

// ── K (Key Button) ────────────────────────────────────────────────────────────
function K({ lbl, sub, onClick, active, wide, danger, accent }) {
  const bg = active ? (accent ? "#1E5E38" : "#9A7A0A") : danger ? "#6A1414" : "#3A3A3A";
  const fg = active ? (accent ? "#C0FFC0" : "#FFFAE0") : danger ? "#FFD0D0" : "#FFF";
  const bb = active ? (accent ? "#0A3820" : "#685808") : danger ? "#420A0A" : "#1A1A1A";
  const w = wide ? 60 : 42;
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: w,
        minWidth: w,
        height: 40,
        background: bg,
        color: fg,
        border: "1px solid #555",
        borderBottom: `3px solid ${bb}`,
        borderRadius: 5,
        cursor: "pointer",
        userSelect: "none",
        flexShrink: 0,
        boxShadow: active ? "none" : "0 2px 3px rgba(0,0,0,0.18)",
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "translateY(2px)";
        e.currentTarget.style.boxShadow = "none";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <span
        style={{
          fontWeight: 700,
          fontSize: "0.74rem",
          lineHeight: 1.1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {lbl}
      </span>
      {sub && (
        <span style={{ fontSize: "0.38rem", opacity: 0.75, marginTop: 1, textAlign: "center" }}>
          {sub}
        </span>
      )}
    </button>
  );
}

function KDiv() {
  return (
    <div
      style={{
        width: 1,
        height: 30,
        background: "rgba(0,0,0,0.15)",
        margin: "0 2px",
        flexShrink: 0,
      }}
    />
  );
}

function KRow({ children, last }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: last ? 0 : 5,
      }}
    >
      {children}
    </div>
  );
}

function LangSel({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        fontSize: "11px",
        border: "1px solid #5A3E20",
        borderRadius: 3,
        padding: "2px 4px",
        background: "#3A2810",
        color: "#E8D8A0",
      }}
    >
      {LANGS.map((l) => (
        <option key={l.code} value={l.code}>
          {l.label}
        </option>
      ))}
    </select>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [leftPct,      setLeftPct]      = useState(64);
  const [headers,      setHeaders]      = useState(INIT_HDR());
  const [sections,     setSections]     = useState(INIT_SECS());
  const [footnote,     setFootnote]     = useState("");
  const [saRight,      setSaRight]      = useState("");
  const [activeSecId,  setActiveSecId]  = useState("pallavi");
  const [activeRowIdx, setActiveRowIdx] = useState(0);
  const [activePane,   setActivePane]   = useState("swara");
  const [cursorPos,    setCursorPos]    = useState(0);
  const [selection,    setSelection]    = useState(null);
  const [notLang,      setNotLang]      = useState("en");
  const [octave,       setOctave]       = useState(null);
  const [speed,        setSpeed]        = useState(0);
  const [numMode,      setNumMode]      = useState(null);
  const [umbrella,     setUmbrella]     = useState(null);
  const [gamakaType,   setGamakaType]   = useState(null);
  const [caps,         setCaps]         = useState(false);
  const [savePanel,    setSavePanel]    = useState(null);
  const [saveName,     setSaveName]     = useState("");
  const [saveMsg,      setSaveMsg]      = useState("");
  const [library,      setLibrary]      = useState({}); // eslint-disable-line no-unused-vars

  const histRef = useRef([]);
  const histIdx = useRef(-1);
  const dragRef = useRef(false);

  // ── Divider drag ──────────────────────────────────────────────────────────
  const onDividerDown = useCallback(() => {
    dragRef.current = true;
    const mv = (e) => {
      if (!dragRef.current) return;
      setLeftPct(Math.max(38, Math.min(76, (e.clientX / window.innerWidth) * 100)));
    };
    const up = () => {
      dragRef.current = false;
      window.removeEventListener("mousemove", mv);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
  }, []);

  // ── Persist ───────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
      if (d) {
        if (d.headers)  setHeaders(d.headers);
        if (d.sections) setSections(d.sections);
        if (d.footnote  !== undefined) setFootnote(d.footnote);
        if (d.saRight   !== undefined) setSaRight(d.saRight);
      }
      setLibrary(JSON.parse(localStorage.getItem(LIBRARY_KEY) || "{}"));
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ headers, sections, footnote, saRight }));
    } catch (e) {}
  }, [headers, sections, footnote, saRight]);

  // ── Save As ───────────────────────────────────────────────────────────────
  const doSaveAs = useCallback(() => {
    const name = (saveName || "carnatikala").trim().replace(/[/\\:*?"<>|]/g, "_");
    const data = { headers, sections, footnote, saRight, savedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = name.endsWith(".json") ? name : `${name}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    try {
      const lib = JSON.parse(localStorage.getItem(LIBRARY_KEY) || "{}");
      lib[name] = { ...data };
      localStorage.setItem(LIBRARY_KEY, JSON.stringify(lib));
      setLibrary(lib);
    } catch (e) {}
    setSaveMsg(`Saved as "${name}.json"`);
    setTimeout(() => { setSaveMsg(""); setSavePanel(null); }, 2000);
  }, [saveName, headers, sections, footnote, saRight]);

  const doImportJSON = useCallback(() => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = ".json";
    inp.onchange = (ev) => {
      const f = ev.target.files[0];
      if (!f) return;
      const r = new FileReader();
      r.onload = (e) => {
        try {
          const d = JSON.parse(e.target.result);
          if (d.headers)  setHeaders(d.headers);
          if (d.sections) setSections(d.sections);
          if (d.footnote  !== undefined) setFootnote(d.footnote);
          if (d.saRight   !== undefined) setSaRight(d.saRight);
          setCursorPos(0);
          setSelection(null);
          setActiveRowIdx(0);
          setActiveSecId("pallavi");
          setActivePane("swara");
        } catch {
          alert("Invalid JSON file.");
        }
      };
      r.readAsText(f);
    };
    document.body.appendChild(inp);
    inp.click();
    document.body.removeChild(inp);
  }, []);

  // ── History ───────────────────────────────────────────────────────────────
  const pushHist = useCallback((secs) => {
    const snap = JSON.parse(JSON.stringify(secs));
    const h = histRef.current.slice(0, histIdx.current + 1);
    h.push(snap);
    if (h.length > 80) h.shift();
    histRef.current = h;
    histIdx.current = h.length - 1;
  }, []);

  const undo = useCallback(() => {
    if (histIdx.current <= 0) return;
    histIdx.current--;
    setSections(JSON.parse(JSON.stringify(histRef.current[histIdx.current])));
  }, []);

  // ── Section / row helpers ─────────────────────────────────────────────────
  const getActiveSec = useCallback(
    () => sections.find((s) => s.id === activeSecId) ?? sections[0],
    [sections, activeSecId]
  );

  const getActiveRow = useCallback(() => {
    const s = getActiveSec();
    return s.rows[activeRowIdx] ?? s.rows[0];
  }, [getActiveSec, activeRowIdx]);

  const updateToks = useCallback(
    (secId, rowIdx, toks) => {
      setSections((prev) => {
        const next = prev.map((s) =>
          s.id !== secId
            ? s
            : { ...s, rows: s.rows.map((r, i) => (i === rowIdx ? { ...r, tokens: toks } : r)) }
        );
        pushHist(next);
        return next;
      });
    },
    [pushHist]
  );

  const updateSah = useCallback((secId, rowIdx, text) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id !== secId
          ? s
          : { ...s, rows: s.rows.map((r, i) => (i === rowIdx ? { ...r, sahityam: text } : r)) }
      )
    );
  }, []);

  const insertTok = useCallback(
    (tok) => {
      const sec = getActiveSec();
      const row = getActiveRow();
      updateToks(sec.id, activeRowIdx, [
        ...row.tokens.slice(0, cursorPos),
        tok,
        ...row.tokens.slice(cursorPos),
      ]);
      setCursorPos((p) => p + 1);
      setSelection(null);
    },
    [getActiveSec, getActiveRow, cursorPos, activeRowIdx, updateToks]
  );

  const addSwara = useCallback(
    (sk, forceCaps) => {
      if (activePane !== "swara") return;
      const useCaps = forceCaps !== undefined ? forceCaps : caps;
      insertTok({
        id: uid(),
        type: "swara",
        swaraKey: sk,
        octave,
        speed,
        sup: null,
        sub: null,
        gid: umbrella?.gid ?? null,
        gdir: umbrella?.dir ?? null,
        gamakaType: umbrella?.gamakaType ?? null,
        _caps: useCaps,
      });
    },
    [activePane, caps, octave, speed, umbrella, insertTok]
  );

  const addSep = useCallback(
    (text) => {
      if (activePane !== "swara") return;
      insertTok({ id: uid(), type: "sep", text });
    },
    [activePane, insertTok]
  );

  const handleNum = useCallback(
    (n) => {
      if (activePane !== "swara") return;
      if (numMode === "sup" || numMode === "sub") {
        const sec  = getActiveSec();
        const row  = getActiveRow();
        const toks = row.tokens;
        let li = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
          if (toks[i]?.type === "swara") { li = i; break; }
        }
        if (li >= 0) {
          const f = numMode === "sup" ? "sup" : "sub";
          updateToks(
            sec.id,
            activeRowIdx,
            toks.map((t, i) => (i === li ? { ...t, [f]: t[f] === n ? null : n } : t))
          );
        }
        setNumMode(null);
      } else {
        addSep(n);
      }
    },
    [activePane, numMode, getActiveSec, getActiveRow, cursorPos, activeRowIdx, updateToks, addSep]
  );

  const deletePrev = useCallback(() => {
    if (activePane !== "swara") return;
    const sec  = getActiveSec();
    const row  = getActiveRow();
    const toks = row.tokens;
    if (selection) {
      const lo  = Math.min(selection.anchor, selection.focus);
      const hi  = Math.max(selection.anchor, selection.focus);
      const del = new Set();
      for (let i = lo; i < hi; i++) {
        const t = toks[i];
        if (!t) continue;
        if (t.gid) toks.filter((x) => x.gid === t.gid).forEach((x) => del.add(x.id));
        else del.add(t.id);
      }
      updateToks(sec.id, activeRowIdx, toks.filter((t) => !del.has(t.id)));
      setCursorPos(lo);
      setSelection(null);
      return;
    }
    if (cursorPos === 0) return;
    const tb = toks[cursorPos - 1];
    if (tb?.gid) {
      const gid = tb.gid;
      const rem = toks.filter((t) => t.gid === gid).length;
      updateToks(sec.id, activeRowIdx, toks.filter((t) => t.gid !== gid));
      setCursorPos((p) => p - rem);
    } else {
      updateToks(sec.id, activeRowIdx, [
        ...toks.slice(0, cursorPos - 1),
        ...toks.slice(cursorPos),
      ]);
      setCursorPos((p) => p - 1);
    }
  }, [activePane, getActiveSec, getActiveRow, cursorPos, selection, activeRowIdx, updateToks]);

  const deleteRow = useCallback(() => {
    if (!window.confirm("Do you want to delete this Swara–Sāhityam row?\nOK = Yes · Cancel = No"))
      return;
    const sec = getActiveSec();
    if (sec.rows.length === 1) {
      updateToks(sec.id, 0, []);
      updateSah(sec.id, 0, "");
      return;
    }
    setSections((prev) => {
      const next = prev.map((s) => {
        if (s.id !== sec.id) return s;
        return { ...s, rows: s.rows.filter((_, i) => i !== activeRowIdx) };
      });
      pushHist(next);
      return next;
    });
    setActiveRowIdx((r) => Math.max(0, r - 1));
    setCursorPos(0);
    setSelection(null);
  }, [getActiveSec, activeRowIdx, updateToks, updateSah, pushHist]);

  const moveLeft = useCallback(() => {
    if (activePane !== "swara") return;
    const toks = getActiveRow().tokens;
    if (cursorPos <= 0) return;
    const tb = toks[cursorPos - 1];
    if (tb?.gid) {
      const gid = tb.gid;
      let i = cursorPos - 1;
      while (i > 0 && toks[i - 1]?.gid === gid) i--;
      setCursorPos(i);
    } else {
      setCursorPos((p) => p - 1);
    }
    setSelection(null);
  }, [activePane, getActiveRow, cursorPos]);

  const moveRight = useCallback(() => {
    if (activePane !== "swara") return;
    const toks = getActiveRow().tokens;
    if (cursorPos >= toks.length) return;
    const ta = toks[cursorPos];
    if (ta?.gid) {
      const gid = ta.gid;
      let i = cursorPos;
      while (i < toks.length && toks[i]?.gid === gid) i++;
      setCursorPos(i);
    } else {
      setCursorPos((p) => p + 1);
    }
    setSelection(null);
  }, [activePane, getActiveRow, cursorPos]);

  const toggleUmb = useCallback(
    (dir) => {
      setUmbrella((u) => {
        if (u?.dir === dir) return null;
        const gt = gamakaType ?? "OSC";
        setGamakaType(gt);
        return { dir, gid: uid(), gamakaType: gt };
      });
    },
    [gamakaType]
  );

  const activateRow = useCallback(
    (secId, rowIdx, pane = "swara", cp) => {
      setActiveSecId(secId);
      setActiveRowIdx(rowIdx);
      setActivePane(pane);
      if (cp != null) {
        setCursorPos(cp);
      } else {
        const s = sections.find((x) => x.id === secId) ?? sections[0];
        const r = s.rows[rowIdx] ?? s.rows[0];
        setCursorPos(r.tokens.length);
      }
      setSelection(null);
    },
    [sections]
  );

  const enterSwara = useCallback(() => setActivePane("sahityam"), []);

  const enterSahityam = useCallback(() => {
    const sec    = getActiveSec();
    const newRow = INIT_ROW();
    setSections((prev) => {
      const next = prev.map((s) => {
        if (s.id !== sec.id) return s;
        const rows = [...s.rows];
        rows.splice(activeRowIdx + 1, 0, newRow);
        return { ...s, rows };
      });
      pushHist(next);
      return next;
    });
    setActiveRowIdx((r) => r + 1);
    setCursorPos(0);
    setActivePane("swara");
  }, [getActiveSec, activeRowIdx, pushHist]);

  // ── Click-to-cursor ───────────────────────────────────────────────────────
  const handleRowClick = useCallback(
    (e, sec, rowIdx, toks) => {
      if (activePane.startsWith("secname")) return;
      setActiveSecId(sec.id);
      setActiveRowIdx(rowIdx);
      setActivePane("swara");
      const els = Array.from(e.currentTarget.querySelectorAll("[data-token-idx]"));
      if (!els.length) { setCursorPos(0); setSelection(null); return; }
      const cx = e.clientX;
      let best = toks.length;
      let bestD = Infinity;
      els.forEach((el) => {
        const fi   = parseInt(el.getAttribute("data-token-idx"));
        const rect = el.getBoundingClientRect();
        if (Math.abs(cx - rect.left)  < bestD) { bestD = Math.abs(cx - rect.left);  best = fi; }
        if (Math.abs(cx - rect.right) < bestD) { bestD = Math.abs(cx - rect.right); best = fi + 1; }
      });
      if (best > 0 && toks[best - 1]?.gid) {
        const gid = toks[best - 1].gid;
        let j = best;
        while (j < toks.length && toks[j]?.gid === gid) j++;
        best = j;
      }
      if (e.shiftKey) {
        setSelection((pv) =>
          pv ? { anchor: pv.anchor, focus: best } : { anchor: cursorPos, focus: best }
        );
      } else {
        setCursorPos(best);
        setSelection(null);
      }
    },
    [activePane, cursorPos]
  );

  // ── Physical keyboard ─────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault(); undo(); return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "Backspace" || e.key === "Delete")) {
        e.preventDefault(); deleteRow(); return;
      }
      if (activePane !== "swara") {
        if (e.key === "Escape") { setActivePane("swara"); e.preventDefault(); }
        return;
      }
      if (e.key === "ArrowLeft")  { e.preventDefault(); moveLeft();  return; }
      if (e.key === "ArrowRight") { e.preventDefault(); moveRight(); return; }
      if (e.key === "CapsLock")   { setCaps(e.getModifierState("CapsLock")); return; }
      if (e.key === "[") { e.preventDefault(); toggleUmb("above"); return; }
      if (e.key === "]") { e.preventDefault(); toggleUmb("below"); return; }
      if (e.key === "'") { e.preventDefault(); setNumMode((m) => (m === "sup" ? null : "sup")); return; }
      if (e.key === "`") { e.preventDefault(); setNumMode((m) => (m === "sub" ? null : "sub")); return; }
      if (e.key === "\\") { e.preventDefault(); addSep("||"); return; }
      if (e.key === "|")  { e.preventDefault(); addSep("|");  return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "q") { setOctave((o) => (o === "up"   ? null : "up"));   return; }
      if (k === "w") { setOctave((o) => (o === "down" ? null : "down")); return; }
      if (k === "=") { setSpeed((s) => (s === 0 ? 1 : s === 1 ? 2 : 0)); return; }
      if (SWARA_KEYS.includes(k)) {
        e.preventDefault();
        const shiftCaps = e.shiftKey ? !caps : caps;
        addSwara(k, shiftCaps);
        return;
      }
      if (/^[0-9]$/.test(k)) { handleNum(k); return; }
      if (k === ",")       { addSep(",");  return; }
      if (k === ";")       { addSep(";");  return; }
      if (k === " ")       { e.preventDefault(); addSep(" "); return; }
      if (k === "-")       { addSep("—");  return; }
      if (k === "enter")   { e.preventDefault(); enterSwara(); return; }
      if (k === "backspace" || k === "delete") { e.preventDefault(); deletePrev(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    activePane, addSwara, addSep, handleNum, moveLeft, moveRight,
    deletePrev, deleteRow, undo, toggleUmb, enterSwara, caps,
  ]);

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderSwaraRow = (sec, rowIdx) => {
    const row   = sec.rows[rowIdx];
    const isAct = activeSecId === sec.id && activeRowIdx === rowIdx && activePane === "swara";
    const gs    = groupTokens(row.tokens);
    const selLo = selection ? Math.min(selection.anchor, selection.focus) : 0;
    const selHi = selection ? Math.max(selection.anchor, selection.focus) : 0;
    const els   = [];

    if (isAct && cursorPos === 0) els.push(<BlinkCursor key="c0" />);

    for (const g of gs) {
      if (g.kind === "arc") {
        els.push(
          <ArcGroup
            key={g.items[0].id}
            dir={g.dir}
            gt={g.gt}
            items={g.items}
            lang={notLang}
            caps={caps}
            startFlat={g.startFlat}
            onClick={(e) => {
              const dest = g.endFlat + 1;
              setActiveSecId(sec.id);
              setActiveRowIdx(rowIdx);
              setActivePane("swara");
              if (e.shiftKey) {
                setSelection((pv) =>
                  pv ? { anchor: pv.anchor, focus: dest } : { anchor: cursorPos, focus: dest }
                );
              } else {
                setCursorPos(dest);
                setSelection(null);
              }
            }}
          />
        );
        const fi = g.endFlat + 1;
        if (isAct && cursorPos === fi) els.push(<BlinkCursor key={"c" + fi} />);
      } else {
        const { tok: t, flatIdx: idx } = g;
        const sel = !!(selection && isAct && idx >= selLo && idx < selHi);
        if (isAct && cursorPos === idx) els.push(<BlinkCursor key={"cb" + idx} />);
        els.push(
          <span
            key={t.id}
            style={{
              display: "inline-flex",
              background: sel ? "rgba(0,120,215,0.18)" : "transparent",
              outline: sel ? "1px solid rgba(0,120,215,0.4)" : "none",
            }}
          >
            {t.type === "swara" ? (
              <SwaraSpan
                t={t}
                lang={notLang}
                caps={t._caps !== undefined ? t._caps : caps}
                dataIdx={idx}
              />
            ) : (
              <SepSpan t={t} dataIdx={idx} />
            )}
          </span>
        );
        if (isAct && cursorPos === idx + 1) els.push(<BlinkCursor key={"ca" + idx} />);
      }
    }

    return (
      <div
        onClick={(e) => handleRowClick(e, sec, rowIdx, row.tokens)}
        style={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "flex-end",
          minHeight: 56,
          padding: "3px 0",
          cursor: "text",
          overflowX: "auto",
        }}
      >
        {els.length ? (
          els
        ) : isAct ? (
          <BlinkCursor />
        ) : (
          <span
            style={{
              opacity: 0.12,
              fontSize: "12px",
              fontStyle: "italic",
              color: INK,
              paddingTop: PAD_TOP,
            }}
          >
            —
          </span>
        )}
      </div>
    );
  };

  const renderSahRow = (sec, rowIdx) => {
    const row   = sec.rows[rowIdx];
    const isAct = activeSecId === sec.id && activeRowIdx === rowIdx && activePane === "sahityam";
    return (
      <textarea
        value={row.sahityam}
        onChange={(e) => updateSah(sec.id, rowIdx, e.target.value)}
        onFocus={() => activateRow(sec.id, rowIdx, "sahityam")}
        placeholder="Sāhityam…"
        rows={1}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            enterSahityam();
          } else if (e.key === "Tab") {
            e.preventDefault();
            const ta = e.target;
            const s  = ta.selectionStart;
            const nv = row.sahityam.slice(0, s) + "   " + row.sahityam.slice(ta.selectionEnd);
            updateSah(sec.id, rowIdx, nv);
            setTimeout(() => { ta.selectionStart = ta.selectionEnd = s + 3; }, 0);
          }
        }}
        style={{
          width: "100%",
          fontFamily: LANG_FONTS[notLang] || LANG_FONTS.en,
          fontSize: "14px",
          color: INK,
          background: isAct ? "#FFFEF4" : "transparent",
          border: "none",
          borderTop: "1px dashed rgba(90,48,16,0.12)",
          padding: "2px 3px",
          resize: "none",
          outline: "none",
          lineHeight: 1.55,
          overflow: "hidden",
        }}
      />
    );
  };

  const renderSection = (sec) => (
    <div key={sec.id} style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 3 }}>
        {activePane === `secname-${sec.id}` ? (
          <input
            autoFocus
            value={sec.name}
            onChange={(e) =>
              setSections((p) =>
                p.map((s) => (s.id === sec.id ? { ...s, name: e.target.value } : s))
              )
            }
            onBlur={() => setActivePane("swara")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape") setActivePane("swara");
            }}
            style={{
              fontFamily: "'Times New Roman',serif",
              fontSize: "0.7rem",
              color: "#5A3010",
              fontWeight: 600,
              textAlign: "center",
              border: "none",
              borderBottom: "1.5px solid #C9A843",
              background: "transparent",
              width: 180,
            }}
          />
        ) : (
          <span
            onClick={() => activateRow(sec.id, 0)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setActiveSecId(sec.id);
              setActivePane(`secname-${sec.id}`);
            }}
            title="Click · Double-click to rename"
            style={{
              fontFamily: "'Times New Roman',serif",
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.16em",
              cursor: "pointer",
              textTransform: "uppercase",
              color:
                activeSecId === sec.id && activePane === "swara" ? "#7A3808" : "#B0946A",
            }}
          >
            {sec.name}
          </span>
        )}
      </div>
      {sec.rows.map((row, ri) => {
        const isActRow = activeSecId === sec.id && activeRowIdx === ri;
        return (
          <div
            key={row.id}
            style={{
              border: `1px solid ${isActRow ? "rgba(201,168,67,0.55)" : "rgba(90,48,16,0.08)"}`,
              borderRadius: 4,
              padding: "3px 6px 2px",
              marginBottom: 2,
              background: isActRow ? "#FFFEF8" : "#FFFFFF",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 1,
                right: 3,
                fontSize: "9px",
                fontWeight: "600",
                color: "rgba(90,48,16,0.18)",
                userSelect: "none",
                pointerEvents: "none",
                fontFamily: "'Times New Roman',serif",
              }}
            >
              www.carnatikala.com
            </div>
            {renderSwaraRow(sec, ri)}
            {renderSahRow(sec, ri)}
          </div>
        );
      })}
    </div>
  );

  const kbActive = activePane === "swara";

  const clearAll = () => {
    if (!window.confirm("Do you want to clear all entries?\nOK = Yes · Cancel = No")) return;
    setSections(INIT_SECS());
    setHeaders(INIT_HDR());
    setFootnote("");
    setSaRight("");
    setCursorPos(0);
    setActiveRowIdx(0);
    setActiveSecId("pallavi");
    setActivePane("swara");
    setSelection(null);
  };

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Tamil&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; background: #F5F0E8; overflow: hidden; }
        textarea, input { outline: none; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; overflow: auto; }
          span[style*="border-radius"] {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <datalist id="ragam-dl">
        {RAGAMS.map((r) => <option key={r} value={r} />)}
      </datalist>
      <datalist id="talam-dl">
        {TALAMS.map((t) => <option key={t} value={t} />)}
      </datalist>

      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#F5F0E8",
          maxWidth: "100vw",
          overflow: "hidden",
        }}
      >
        {/* ── Top bar ── */}
        <div
          className="no-print"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            background: "#2E1E0A",
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "Georgia,serif",
              color: "#D4A843",
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
              marginRight: 4,
            }}
          >
            Carnatikala
          </span>
          <span style={{ fontSize: "9px", color: "#A08050" }}>Notation:</span>
          <LangSel value={notLang} onChange={setNotLang} />
          <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.15)", margin: "0 2px" }} />
          <button
            onClick={() => { setSavePanel((p) => (p === "save" ? null : "save")); setSaveName(""); setSaveMsg(""); }}
            style={{
              padding: "2px 7px", fontSize: "10px", background: "#2A4A2A",
              color: "#C0FFC0", border: "1px solid #3A6A3A", borderRadius: 3,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            💾 Save As
          </button>
          <button
            onClick={doImportJSON}
            style={{
              padding: "2px 7px", fontSize: "10px", background: "#2A3A5A",
              color: "#B0C8F0", border: "1px solid #3A5A8A", borderRadius: 3, cursor: "pointer",
            }}
          >
            ↑ Open
          </button>
          <button
            onClick={() => window.print()}
            style={{
              padding: "2px 7px", fontSize: "10px", background: "#3A1A4A",
              color: "#E0C8FF", border: "1px solid #5A3A6A", borderRadius: 3, cursor: "pointer",
            }}
          >
            ⎙ PDF
          </button>
          <button
            onClick={clearAll}
            style={{
              padding: "2px 7px", fontSize: "10px", background: "#6A1414",
              color: "#FFD0D0", border: "1px solid #8A2424", borderRadius: 3,
              cursor: "pointer", marginLeft: "auto",
            }}
          >
            ✕ Clear
          </button>
        </div>

        {/* ── Save As panel ── */}
        {savePanel === "save" && (
          <div
            className="no-print"
            style={{
              background: "#3A2810", padding: "6px 12px", display: "flex",
              gap: 6, alignItems: "center", flexWrap: "wrap", flexShrink: 0,
            }}
          >
            <span style={{ fontSize: "10px", color: "#C9A843" }}>Save As:</span>
            <input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") doSaveAs();
                if (e.key === "Escape") setSavePanel(null);
              }}
              placeholder="Enter file name…"
              style={{
                flex: 1, minWidth: 120, maxWidth: 220,
                border: "1px solid #6A5030", borderRadius: 3,
                padding: "3px 7px", background: "#FFF8E8",
                fontFamily: "'Times New Roman',serif", fontSize: "12px", color: "#2E1E0A",
              }}
            />
            <button
              onClick={doSaveAs}
              style={{
                padding: "3px 12px", fontSize: "11px", background: "#8B6D14",
                color: "#FFF8E0", border: "none", borderRadius: 3, cursor: "pointer",
              }}
            >
              Save
            </button>
            <button
              onClick={() => setSavePanel(null)}
              style={{
                padding: "3px 8px", fontSize: "11px", background: "#555",
                color: "#FFF", border: "none", borderRadius: 3, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            {saveMsg && <span style={{ fontSize: "11px", color: "#90D890" }}>{saveMsg}</span>}
            <span style={{ fontSize: "9px", color: "#A08050", marginLeft: 4 }}>
              File downloads to your computer's Downloads folder as JSON.
            </span>
          </div>
        )}

        {/* ── Header fields ── */}
        <div
          style={{
            background: "#FEFCF5",
            borderBottom: "1px solid #D8CEB8",
            padding: "5px 10px",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {HEADER_FIELDS.map(({ key, label, list }) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                <span
                  style={{
                    fontFamily: "'Times New Roman',serif",
                    fontSize: "10px",
                    color: "#8A6040",
                    fontStyle: "italic",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}:
                </span>
                <input
                  value={headers[key]}
                  list={list || undefined}
                  onChange={(e) => setHeaders((h) => ({ ...h, [key]: e.target.value }))}
                  onFocus={() => setActivePane("header")}
                  onBlur={() => setActivePane("swara")}
                  placeholder={label}
                  style={{
                    width:
                      key === "songname"
                        ? 100
                        : key === "arohanam" || key === "avarohanam"
                        ? 90
                        : 76,
                    border: "none",
                    borderBottom: "1px solid #D0C4A8",
                    background: "transparent",
                    fontFamily: "'Times New Roman',serif",
                    fontSize: "12px",
                    color: "#2E1E0A",
                    padding: "1px 2px",
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── Main split ── */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

          {/* Left — notation */}
          <div
            style={{
              width: `${leftPct}%`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <div style={{ flex: 1, overflowY: "auto", padding: "7px 8px 5px 10px", minHeight: 0 }}>
              {sections.map(renderSection)}

              {/* Footnote */}
              <div style={{ marginTop: 5 }}>
                <div
                  style={{
                    fontSize: "9px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#8A6040",
                    marginBottom: 2,
                  }}
                >
                  Footnotes
                </div>
                <textarea
                  value={footnote}
                  onChange={(e) => setFootnote(e.target.value)}
                  onFocus={() => setActivePane("footnote")}
                  onBlur={() => setActivePane("swara")}
                  rows={2}
                  style={{
                    width: "100%",
                    fontFamily: "'Times New Roman',serif",
                    fontSize: "12px",
                    color: "#2E1E0A",
                    background: "#FEFCF5",
                    border: "1px solid #D8CEB8",
                    borderRadius: 4,
                    padding: "4px 6px",
                    resize: "vertical",
                    lineHeight: 1.5,
                  }}
                />
              </div>
            </div>

            {/* ── Keyboard ── */}
            <div
              className="no-print"
              style={{
                flexShrink: 0,
                padding: "5px 7px 6px",
                background: "#DEDAD0",
                borderTop: "1px solid #C4BAA8",
                position: "relative",
              }}
            >
              {/* Haze overlay when keyboard inactive */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(222,218,208,0.86)",
                  zIndex: 10,
                  pointerEvents: "none",
                  opacity: kbActive ? 0 : 1,
                  transition: "opacity 0.2s",
                }}
              />
              {!kbActive && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 11,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <span
                    style={{
                      background: "rgba(255,253,245,0.97)",
                      border: "1px solid #D0C4A8",
                      borderRadius: 5,
                      padding: "3px 12px",
                      fontFamily: "'Times New Roman',serif",
                      fontSize: "10px",
                      color: "#5A3010",
                      fontStyle: "italic",
                    }}
                  >
                    {activePane === "sahityam"
                      ? "Sāhityam: Tab aligns · Enter → next row · Esc returns"
                      : activePane === "footnote"
                      ? "Footnotes — Esc returns"
                      : "Header field — Esc returns"}
                  </span>
                </div>
              )}

              {/* Row 1 — Modifiers + Arcs + Gamaka + Enter + Delete */}
              <KRow>
                <K lbl={<TaraIcon />}  onClick={() => setOctave((o) => (o === "up"   ? null : "up"))}   active={octave === "up"}   sub="Q" />
                <K lbl={<ManDrIcon />} onClick={() => setOctave((o) => (o === "down" ? null : "down"))} active={octave === "down"} sub="W" />
                <KDiv />
                <K lbl={<SngLine />} onClick={() => setSpeed((s) => (s === 1 ? 0 : 1))} active={speed === 1} />
                <K lbl={<DblLine />} onClick={() => setSpeed((s) => (s === 2 ? 0 : 2))} active={speed === 2} />
                <KDiv />
                <K lbl={<SupIcon />} onClick={() => setNumMode((m) => (m === "sup" ? null : "sup"))} active={numMode === "sup"} sub="'" />
                <K lbl={<SubIcon />} onClick={() => setNumMode((m) => (m === "sub" ? null : "sub"))} active={numMode === "sub"} sub="`" />
                <KDiv />
                <K lbl={<ArcAbv />} onClick={() => toggleUmb("above")} active={umbrella?.dir === "above"} accent={umbrella?.dir === "above"} sub="[" />
                <K lbl={<ArcBlw />} onClick={() => toggleUmb("below")} active={umbrella?.dir === "below"} accent={umbrella?.dir === "below"} sub="]" />
                <KDiv />
                {GAMAKA_TYPES.map((gt) => (
                  <K
                    key={gt}
                    lbl={gt}
                    onClick={() => {
                      const next = gamakaType === gt ? null : gt;
                      setGamakaType(next);
                      if (next) {
                        setUmbrella((u) =>
                          u ? { ...u, gamakaType: next } : { dir: "above", gid: uid(), gamakaType: next }
                        );
                      } else {
                        setUmbrella(null);
                      }
                    }}
                    active={gamakaType === gt}
                    accent={gamakaType === gt}
                  />
                ))}
                <KDiv />
                <K lbl="↵" onClick={enterSwara} wide />
                <K lbl="⌫" onClick={deletePrev} danger />
              </KRow>

              {/* Row 2 — Caps + Swaras + Separators */}
              <KRow>
                {notLang === "en" && (
                  <>
                    <K lbl={caps ? "CAPS" : "caps"} onClick={() => setCaps((c) => !c)} active={caps} sub="⇪" />
                    <KDiv />
                  </>
                )}
                {SWARA_KEYS.map((k) => (
                  <K key={k} lbl={getSwaraText(k, notLang, caps)} onClick={() => addSwara(k)} />
                ))}
                <KDiv />
                <K lbl="||" onClick={() => addSep("||")} sub="\\" />
                <K lbl="|"  onClick={() => addSep("|")}  sub="⇧\\" />
                <K lbl={<span style={{ fontSize: "14px", fontWeight: "bold" }}>,</span>} onClick={() => addSep(",")} />
                <K lbl={<span style={{ fontSize: "14px", fontWeight: "bold" }}>;</span>} onClick={() => addSep(";")} />
                <K lbl="—"  onClick={() => addSep("—")} />
                <K lbl="⎵"  onClick={() => addSep(" ")} wide />
              </KRow>

              {/* Row 3 — Numerals + Delete row */}
              <KRow last>
                {["0","1","2","3","4","5","6","7","8","9"].map((n) => (
                  <K
                    key={n}
                    lbl={numMode === "sup" ? SUP[n] : numMode === "sub" ? SUB[n] : n}
                    onClick={() => handleNum(n)}
                  />
                ))}
                <KDiv />
                <K lbl="🗑" sub="⌘⌫ row" onClick={deleteRow} danger />
              </KRow>
            </div>
          </div>

          {/* Divider */}
          <div
            onMouseDown={onDividerDown}
            style={{
              width: 4,
              background: "#C8BEA8",
              cursor: "col-resize",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#9A7050")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#C8BEA8")}
          />

          {/* Right — Sāhityam */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background: "#FEFCF5",
              borderLeft: "1px solid #D8CEB8",
              minWidth: 0,
            }}
          >
            <div
              style={{
                padding: "4px 8px",
                borderBottom: "1px solid #D8CEB8",
                background: "#F8F4EC",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: "'Times New Roman',serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#5A3010",
                  letterSpacing: "0.1em",
                }}
              >
                SĀHITYAM
              </span>
            </div>
            <textarea
              value={saRight}
              onChange={(e) => setSaRight(e.target.value)}
              onFocus={() => setActivePane("saright")}
              onBlur={() => setActivePane("swara")}
              placeholder={
                "Type or paste the full Sāhityam here.\n\nCopy individual lines to the Sāhityam rows on the left.\n\nNormal keyboard is active here."
              }
              style={{
                flex: 1,
                fontFamily: LANG_FONTS[notLang] || LANG_FONTS.en,
                fontSize: "13px",
                color: "#2E1E0A",
                background: "transparent",
                border: "none",
                padding: "8px 10px",
                resize: "none",
                lineHeight: 1.9,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}